/// <amd-module name="Build/Designer/RepositorySelector" />
import * as Build from "Build/Client/Contracts/Build";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import * as Resources from "Build/Designer/Resources";
import { IWizardListItem, WizardList } from "Build/Designer/WizardList";
import { SelectionMode } from "OfficeFabric/Selection";
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { traceWarning } from "VSS/Platform/Trace";
import { Ago } from "VSSUI/Ago";
import { FilterBar } from "VSSUI/FilterBar";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IPickListItem, PickListFilterBarItem } from "VSSUI/PickList";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { Filter, FILTER_CHANGE_EVENT, IFilterState } from "VSSUI/Utilities/Filter";

type FilterResultSet = Build.ResultSet | string;

export interface Props {
    connectionId?: string;
    sourceProvider: string;
    onSelectRepository: (repository: Build.SourceRepository, properties: { [key: string]: string }) => void;
    onRepositoryListUpdate: (action: string, properties?: { [key: string]: any }) => void;
    strongBoxKey?: string;
    autoSelectRepositoryId?: string;
    onErrorMessage: (errorMessage: string) => void;
    onTTI: () => void;
}

export interface State {
    projectId: string;
    projectName: string;
    filterText: string | undefined;
    filterNoReposFound: boolean;
    filterTypes: FilterItem[] | undefined;
    footerText: JSX.Element | undefined;
    repositories: Build.SourceRepository[];
    repositoryList: RepositoriesListData | undefined;
    resultSet: FilterResultSet;
}

export interface RepositoriesListData extends Build.SourceRepositories {
    resultSets?: FilterItem[];
    currentResults?: FilterItem;
    manageAccessUrl?: string;
}

export interface FilterItem {
    key: number | string;
    displayName: string;
}

export class RepositorySelector<P extends Props> extends VssComponent<P, State, IVssComponentContext> {
    private _filterBarRef = React.createRef<FilterBar>();

    private _client: IBuildRestClient;
    private _mounted: boolean;
    private _filter: Filter;
    private _ttiMarked: boolean = false;
    private _timer: Date;

    constructor(props: P, context: IVssComponentContext) {
        super(props, context);
        this._client = this.context.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");

        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();

        this.state = {
            projectId: pageData!.project!.id,
            projectName: pageData!.project!.name,
            filterText: undefined,
            filterTypes: undefined,
            footerText: undefined,
            filterNoReposFound: false,
            repositories: [],
            repositoryList: undefined,
            resultSet: Build.ResultSet.Top
        };

        const initialSelection = this.getFilterItems()[0];
        this._filter = new Filter({
            defaultState: {
                filterType: {
                    value: !!initialSelection ? [initialSelection] : undefined
                }
            }
        });
        this._filter.subscribe(this._onFilterChanged, FILTER_CHANGE_EVENT);
    }

    public render(): JSX.Element {
        return (
            <div className={this.getClassName("repository-selection")}>
                {
                    <div className="header-section">
                        <div className="subheader body-m secondary-text">{Resources.NewPipeline}</div>
                        <div className="header title-l">{Resources.RepositorySelectorTitle}</div>
                    </div>
                }
                {
                    <FilterBar filter={this._filter} ref={this._filterBarRef}>
                        <KeywordFilterBarItem
                            filterItemKey={"filterText"}
                            placeholder={Resources.RepositorySelectorFilterPlaceholder}
                            throttleWait={400}
                        />
                        {this._showFilterDropdown() && (
                            <PickListFilterBarItem
                                filterItemKey={"filterType"}
                                getListItem={this._getFilterItem}
                                getPickListItems={this._getFilterItems}
                                selectionMode={SelectionMode.single}
                            />
                        )}
                    </FilterBar>
                }
                {this.state.repositoryList && this.renderRepositoryList()}
                {this.state.footerText && (
                    <div className="repository-picker-footer flex-row flex-start">
                        <div className="flex-self-start">
                            <Icon iconName="Error" className="repository-picker-footer-icon" />
                        </div>
                        <div className="repository-picker-footer-text flex-grow scroll-hidden">{this.state.footerText}</div>
                    </div>
                )}
            </div>
        );
    }

    public componentDidMount(): void {
        this._mounted = true;
        this._fetchRepositories();
    }

    public componentWillUnmount(): void {
        this._mounted = false;
    }

    protected getClassName(baseClassName: string): string {
        return baseClassName;
    }

    protected getFilterItems(): FilterItem[] {
        return [];
    }

    protected getFooter(filterNoReposFound: boolean, downloading: boolean): JSX.Element | undefined {
        if (filterNoReposFound) {
            const link = this.getFooterLink();
            return (
                <>
                    <span>{Resources.RepositorySelectorNoneFoundDescription} </span>
                    {link && (
                        <FormatComponent format={Resources.RepositorySelectorProvideAccessDescription}>
                            {<Link href={link}>{Resources.RepositorySelectorProvideAccessDescriptionLink}</Link>}
                        </FormatComponent>
                    )}
                </>
            );
        }
        return undefined;
    }

    protected getFooterLink(): string | undefined {
        return undefined;
    }

    protected getRepositoryIconUrl(repository: Build.SourceRepository): string | undefined {
        return repository && repository.properties && repository.properties.ownerAvatarUrl;
    }

    protected getRepositoryIconName(): string {
        return "GitLogo";
    }

    protected getRepositoryIconClass(): string | undefined {
        return;
    }

    protected compareRepositories(a: Build.SourceRepository, b: Build.SourceRepository): number {
        const timeA: number = Date.parse(a.properties.lastUpdated);
        const timeB: number = Date.parse(b.properties.lastUpdated);
        const delta: number = timeB - timeA; // sort newest first
        return isNaN(delta) ? 0 : delta;
    }

    /**
     * Fetch repositories based on a custom filter sting (implemented by child class)
     */
    protected getRepositories(
        serviceEndpointId?: string,
        filter?: string,
        resultSet?: string,
        pageResults?: boolean,
        continuationToken?: string
    ): Promise<RepositoriesListData> {
        throw new Error("getRepositories() not implemented");
    }

    protected checkIsMounted(): boolean {
        return this._mounted;
    }

    protected autoSelectConnection(): boolean {
        return true;
    }

    private renderRepositoryList = () => {
        const repositories = this._getFilteredRepositories();
        return (
            <WizardList
                wizardListItems={this._mapToWizardListItems(repositories)}
                loadMore={this.loadMore}
                onClick={(event, data) => {
                    const selectedRepository = repositories[data.index];
                    this._onRepoSelected(selectedRepository, getRepositoryProperties(selectedRepository.properties));
                }}
            />
        );
    };

    private _mapToWizardListItems = (repositories: Build.SourceRepository[]): (IWizardListItem | IObservableValue<undefined>)[] => {
        const items = repositories.map<IWizardListItem | IObservableValue<undefined>>(repo => {
            const labels = [];
            if (isPropertyEnabled(repo, "isFork")) {
                labels.push(Resources.RepositorySelectorTagFork);
            }
            if (isPropertyEnabled(repo, "isPrivate")) {
                labels.push(Resources.RepositorySelectorTagPrivate);
            }

            let lastUpdatedDate: Date;
            const lastUpdated = repo.properties && repo.properties.lastUpdated;
            if (lastUpdated) {
                const isValidDate = !isNaN(Date.parse(lastUpdated));
                if (isValidDate) {
                    lastUpdatedDate = new Date(lastUpdated);
                } else {
                    traceWarning(
                        this.context.pageContext,
                        "vss-build-web.designer",
                        "RepositorySelector",
                        format("{0} repository lastUpdated property '{1}' is not a recognized date string", this.props.sourceProvider, lastUpdated)
                    );
                }
            }

            return {
                name: repo.fullName,
                iconUrl: this.getRepositoryIconUrl(repo),
                iconName: this.getRepositoryIconName(),
                iconClass: css("rounded-corners", this.getRepositoryIconClass()),
                labels: labels,
                isFavorite: isPropertyEnabled(repo, "isFavorite"),
                renderDescription: () => {
                    return lastUpdatedDate ? <Ago className="body-s secondary-text" date={lastUpdatedDate} format={AgoFormat.Compact} /> : <></>;
                }
            } as IWizardListItem;
        });

        // Add the loading row that will call back to load the next page
        if (this.state.repositoryList?.continuationToken) {
            items.push(new ObservableValue(undefined));
        }

        return items;
    };

    private _showFilterDropdown(): boolean {
        return this.getFilterItems().length > 0 || Boolean(this.state.filterTypes && this.state.filterTypes.length > 0);
    }

    private _updateFilterDropdown(resultSet: FilterResultSet): boolean {
        return this.autoSelectConnection() && resultSet !== undefined && !this.isCustomResultSetFilter(resultSet);
    }

    private _fetchRepositories(): void {
        this._timer = new Date();

        this.setState({
            footerText: undefined,
            repositories: [],
            resultSet: this.state.resultSet
        });

        const currentResultSet = this.state.resultSet;
        this.listRepositories(this.state.projectId, this.props.sourceProvider, this.props.connectionId, currentResultSet, true).then(
            x => {
                this._onRepositoryListReceived(x, currentResultSet);
            },
            (error: Error) => {
                this.props.onErrorMessage(Resources.InvalidServiceConnection);
                this.setState({
                    footerText: this.getFooter(true, false)
                });
            }
        );
    }

    private listRepositories(
        project: string,
        providerName: string,
        serviceEndpointId?: string,
        resultSet?: FilterResultSet,
        pageResults?: boolean,
        continuationToken?: string
    ): Promise<RepositoriesListData> {
        if (this.autoSelectConnection() || (resultSet !== undefined && this.isCustomResultSetFilter(resultSet))) {
            return this.getRepositories(serviceEndpointId, undefined /* filter */, resultSet as string, pageResults, continuationToken);
        } else {
            return this._client.listRepositories(
                project,
                providerName,
                serviceEndpointId,
                undefined /* repository */,
                resultSet as Build.ResultSet,
                pageResults,
                continuationToken
            );
        }
    }

    private isCustomResultSetFilter(resultSet: FilterResultSet): boolean {
        return typeof resultSet === "string";
    }

    private _onFilterChanged = (newValue: IFilterState, action: string) => {
        if (newValue.hasOwnProperty("filterText")) {
            const filterText = (newValue.filterText && newValue.filterText.value) || "";
            const filterNoReposFound: boolean = this._filterNoReposFound(this.state.repositories || [], filterText);
            this.setState(
                {
                    filterText: filterText,
                    filterNoReposFound: filterNoReposFound,
                    footerText: this.getFooter(filterNoReposFound, !!(this.state.repositoryList && this.state.repositoryList.continuationToken))
                },
                () => this.props.onRepositoryListUpdate("ReposFilter", { repoCount: this._getFilteredRepositories().length, filterText })
            );
        }
        if (newValue.hasOwnProperty("filterType") && newValue.filterType && newValue.filterType.value) {
            const filterType = newValue.filterType && newValue.filterType.value && newValue.filterType.value[0];
            if (filterType && filterType.key !== this.state.resultSet) {
                this.setState(
                    {
                        resultSet: filterType.key,
                        footerText: undefined
                    },
                    () => {
                        this._fetchRepositories();
                    }
                );
            }
        }
    };

    private _getFilterItems = () => {
        return this.state.filterTypes || this.getFilterItems();
    };

    private _getFilterItem = (item: FilterItem): IPickListItem => {
        return {
            name: item.displayName,
            key: item.key
        } as IPickListItem;
    };

    private _onRepositoryListReceived = (sourceRepositories: RepositoriesListData, resultSet: FilterResultSet) => {
        if (this._mounted && resultSet === this.state.resultSet && sourceRepositories) {
            if (sourceRepositories.repositories && this.props.autoSelectRepositoryId) {
                const repository = sourceRepositories.repositories.find(y => y.id === this.props.autoSelectRepositoryId);
                if (repository) {
                    this.props.onSelectRepository(repository, getRepositoryProperties(repository.properties));
                    this._markTTI();
                    return;
                }
            }

            const state: State = {} as State;
            const repositories = (this.state.repositories || []).concat(sourceRepositories.repositories);

            let callback = undefined;

            if (this._updateFilterDropdown(resultSet)) {
                state.filterTypes = sourceRepositories.resultSets || this.getFilterItems();
                // if we have filter types update the filter state during the callback
                if (state.filterTypes && state.filterTypes.length > 0) {
                    callback = () => {
                        const currentResultsKey = sourceRepositories.currentResults && sourceRepositories.currentResults.key;
                        const currentFilter = state.filterTypes!.find(x => x.key === currentResultsKey) || this.state.resultSet;
                        const currentFilterObj = { value: currentFilter && [currentFilter] };
                        this._filter.setDefaultState({ filterType: currentFilterObj });
                        this._filter.setFilterItemState("filterType", currentFilterObj);
                    };
                }
            }

            // Sort by Last Update
            repositories.sort(this.compareRepositories);

            const filterNoReposFound: boolean = this._filterNoReposFound(repositories, this.state.filterText || "");
            state.filterNoReposFound = filterNoReposFound;
            state.footerText = this.getFooter(filterNoReposFound, false);

            // only calling list updated once we are done paging and have the total results
            this.props.onRepositoryListUpdate("ReposLoaded", {
                resultSet: resultSet && resultSet.toString(),
                repoCount: repositories.length,
                duration: this._timer ? new Date().getTime() - this._timer.getTime() : -1
            });

            state.repositoryList = sourceRepositories;
            state.repositories = repositories;
            this.setState(state, callback);
            this._markTTI();
        }
    };

    private loadMore = () => {
        const { repositoryList, resultSet } = this.state;
        if (repositoryList?.continuationToken) {
            this.listRepositories(
                this.state.projectId,
                this.props.sourceProvider,
                this.props.connectionId,
                resultSet,
                true,
                repositoryList?.continuationToken
            ).then(data => this._onRepositoryListReceived(data, resultSet));
        }
    };

    private _onRepoSelected = (repository: Build.SourceRepository, properties: { [key: string]: string }): void => {
        this.props.onSelectRepository(repository, properties);
    };

    private _getFilteredRepositories = (): Build.SourceRepository[] => {
        if (this.state.repositories) {
            return this.state.filterText ? this._filterRepositories(this.state.repositories, this.state.filterText) : this.state.repositories;
        }
        return [];
    };

    private _filterRepositories(repositories: Build.SourceRepository[], filterText: string): Build.SourceRepository[] {
        if (repositories) {
            const searchForString = filterText.trim().toLowerCase();
            return repositories.filter(a => a.fullName.toLowerCase().indexOf(searchForString) !== -1);
        }
        return repositories;
    }

    private _filterNoReposFound(repositories: Build.SourceRepository[], newValue: string): boolean {
        return this._filterRepositories(repositories, newValue).length === 0;
    }

    private _markTTI = () => {
        if (this.props.onTTI && !this._ttiMarked) {
            this._ttiMarked = true;
            this.props.onTTI();
        }
    };
}

function getRepositoryProperties(properties: { [key: string]: string }) {
    const getProperty = (key: string) => properties && properties[key];
    return {
        isFork: getProperty("isFork"),
        isPrivate: getProperty("isPrivate"),
        externalOwnerId: getProperty("ownerId"),
        externalOwnerName: getProperty("orgName"),
        externalOwnerIsAUser: getProperty("ownerIsAUser"),
        repositoryUrl: getProperty("manageUrl"),
        safeRepository: getProperty("safeRepository"),
        hasAdminPermissions: getProperty("hasAdminPermissions")
    };
}

function isPropertyEnabled(repository: Build.SourceRepository, property: string): boolean {
    const propertyValue: string = repository && repository.properties && repository.properties[property];
    return !!(propertyValue && propertyValue.toLowerCase() === "true");
}
